import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import JobDetailsModal from "@/components/JobDetailsModal";
import { toast } from "sonner";
import {
  MessageSquare,
  Send,
  Search,
  Calendar,
  MapPin,
  ArrowLeft,
  Mail,
  MailOpen,
  Archive,
  ArchiveRestore,
  Briefcase
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import AutoGrowTextarea from "@/components/AutoGrowTextarea";
import linkifyText from "@/utils/linkifyText";
import { money, initial, avatarGradient } from "@/utils/fjMeta";
import "@/styles/fj.css";
import "@/styles/commandDeck.css";

// Rank conversations by most-recent real activity. Conversations with no
// messages yet (empty "shell" conversations) sort to the bottom so they never
// jump to the top on first paint. Used by BOTH the initial load and the poll
// so the two orderings always agree (prevents the rows-disappear-after-10s flicker).
const conversationActivityTime = (c) => {
  if (!c.last_message) return 0; // message-less shells go last
  return new Date(c.last_message_time || c.last_message_at || 0).getTime();
};
const sortConversations = (list) =>
  [...list].sort((a, b) => conversationActivityTime(b) - conversationActivityTime(a));

const MessagesRedesigned = ({ onLogout }) => {
  const { isDark } = useTheme();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');
  const messagesEndRef = useRef(null);

  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showJobModal, setShowJobModal] = useState(false);
  const [fullJobData, setFullJobData] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [currentMessages, setCurrentMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [conversationsPage, setConversationsPage] = useState(1);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [showConversationList, setShowConversationList] = useState(true); // Mobile toggle
  const [filterUnread, setFilterUnread] = useState(false); // Filter for unread messages
  const [filterArchived, setFilterArchived] = useState(false); // Filter for archived messages

  useEffect(() => {
    loadConversations(1);
  }, []);

  // Auto-open conversation when navigated from another page
  useEffect(() => {
    if (!location.state?.conversationId || !location.state?.autoOpen) return;
    if (conversations.length === 0) return;

    const targetConversation = conversations.find(c => c.id === location.state.conversationId);
    if (targetConversation) {
      handleSelectConversation(targetConversation);
      setShowConversationList(false); // Hide list on mobile
      // Clear the state to prevent re-opening on refresh
      window.history.replaceState({}, document.title);
    } else {
      loadSpecificConversation(location.state.conversationId);
    }
  }, [location.state, conversations]);

  const loadSpecificConversation = async (conversationId) => {
    try {
      console.log('Loading conversation directly:', conversationId);

      // Fetch the specific conversation's messages
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages/conversation/${conversationId}?user_email=${user.email}`
      );

      console.log('Direct conversation response:', response.data);

      if (response.data.conversation) {
        const convData = response.data.conversation;

        // The backend now returns other_person info
        const conversation = {
          id: conversationId,
          job_id: convData.job_id,
          job_title: convData.job_title || 'Conversation',
          job_date: convData.job_date,
          job_location: convData.job_location,
          other_person: convData.other_person || { name: 'Unknown User', email: '', profile_photo_url: null },
          unread_count: 0,
          last_message: convData.last_message,
          last_message_time: convData.last_message_time
        };

        console.log('Opening conversation:', conversation);

        setSelectedConversation(conversation);
        setCurrentMessages(response.data.messages || []);
        setShowConversationList(false);

        // Clear navigation state
        window.history.replaceState({}, document.title);

        toast.success('Conversation opened');
      }
    } catch (error) {
      console.error('Failed to load specific conversation:', error);
      toast.error('Conversation not found');
    }
  };

  const loadConversations = async (page = 1, unreadOnly = false, archivedOnly = false) => {
    try {
      setLoading(true);
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/messages/conversations?user_email=${user.email || 'test@shootzu.com'}`;

      if (archivedOnly) {
        url += `&archived_only=true&limit=20&page=${page}`;
      } else if (unreadOnly) {
        url += `&unread_only=true`;
      } else {
        url += `&limit=20&page=${page}`;
      }

      const response = await axios.get(url);

      const newConversations = response.data.conversations || [];

      if (page === 1 || unreadOnly || archivedOnly) {
        setConversations(sortConversations(newConversations));
      } else {
        setConversations(prev => sortConversations([...prev, ...newConversations]));
      }

      setHasMoreConversations(!unreadOnly && !archivedOnly && newConversations.length === 20);
      setConversationsPage(page);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      toast.error('Failed to load conversations');
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreConversations = () => {
    loadConversations(conversationsPage + 1);
  };

  const loadConversationMessages = async (conversationId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages/conversation/${conversationId}?user_email=${user.email || 'test@shootzu.com'}`
      );
      setCurrentMessages(response.data.messages || []);

      setConversations(prev => prev.map(conv =>
        conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
      ));
    } catch (error) {
      console.error('Failed to load messages:', error);
      toast.error('Failed to load messages');
      setCurrentMessages([]);
    }
  };

  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setShowConversationList(false); // Hide list on mobile
    await loadConversationMessages(conversation.id);
  };

  const handleBackToList = () => {
    setShowConversationList(true);
    setSelectedConversation(null);
  };

  const handleArchiveConversation = async (e, conversationId) => {
    e.stopPropagation(); // Prevent selecting the conversation
    const token = localStorage.getItem('shootzu_token');
    if (!token) {
      toast.error('Please log in to archive conversations');
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages/conversation/${conversationId}/archive?user_email=${user.email}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      // Remove from local state
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));

      // If the archived conversation was selected, clear selection
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setCurrentMessages([]);
      }

      toast.success('Conversation archived');
    } catch (error) {
      console.error('Failed to archive conversation:', error);
      toast.error('Failed to archive conversation');
    }
  };

  const handleRestoreConversation = async (e, conversationId) => {
    e.stopPropagation(); // Prevent selecting the conversation
    const token = localStorage.getItem('shootzu_token');
    if (!token) {
      toast.error('Please log in to restore conversations');
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages/conversation/${conversationId}/unarchive?user_email=${user.email}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      // Remove from local state (since we're viewing archived)
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));

      // If the restored conversation was selected, clear selection
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setCurrentMessages([]);
      }

      toast.success('Conversation restored');
    } catch (error) {
      console.error('Failed to restore conversation:', error);
      toast.error('Failed to restore conversation');
    }
  };

  const handleViewJobDetails = async () => {
    if (!selectedConversation?.job_id) {
      toast.error('Job information not available');
      return;
    }

    try {
      // Fetch full job details from backend
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/details/${selectedConversation.job_id}`
      );

      if (response.data) {
        setFullJobData(response.data);
        setShowJobModal(true);
      }
    } catch (error) {
      console.error('Failed to fetch job details:', error);
      // Fallback to conversation data
      setFullJobData({
        title: selectedConversation.job_title,
        jobNumber: selectedConversation.job_mysql_id,
        type: selectedConversation.job_type,
        jobDate: selectedConversation.job_date,
        location: selectedConversation.job_location,
        hours: selectedConversation.job_hours || 0,
        ratePerHour: selectedConversation.job_rate || 0,
        total: selectedConversation.job_total || 0,
        brief: selectedConversation.job_description || '',
        status: 'open'
      });
      setShowJobModal(true);
    }
  };

  const handleViewInvoice = () => {
    // Navigate to invoices page
    window.location.href = '/invoices';
  };

  const formatMessageTime = (timestamp, displayTime, displayDate) => {
    // Always prefer backend-provided formatted time
    if (displayTime) return displayTime;

    if (!timestamp) return '';

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';

      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Time format error:', error);
      return '';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getCityState = (fullAddress) => {
    if (!fullAddress) return '';
    // Extract suburb/city and state from full address
    // Format: "123 Street, Suburb VIC 3000, Australia" -> "Suburb VIC"
    const parts = fullAddress.split(',').map(p => p.trim());

    // Find part with state abbreviation (VIC, NSW, QLD, etc.)
    const statePattern = /\b(VIC|NSW|QLD|SA|WA|TAS|NT|ACT)\b/;
    const cityStatePart = parts.find(p => statePattern.test(p));

    if (cityStatePart) {
      // Extract just "Suburb VIC" or "City NSW"
      const match = cityStatePart.match(/^([^0-9]+)\s+(VIC|NSW|QLD|SA|WA|TAS|NT|ACT)/);
      if (match) {
        return `${match[1].trim()} ${match[2]}`;
      }
      return cityStatePart.split(/\d/)[0].trim(); // Remove postal code
    }

    return parts[0] || fullAddress;
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation || sendingMessage) return;

    const token = localStorage.getItem('shootzu_token');
    if (!token) {
      toast.error('Please log in to send messages');
      return;
    }

    try {
      setSendingMessage(true);

      const userId = user.id || user.email;
      const otherPerson = selectedConversation.other_person;
      const otherUserId = otherPerson?.id || otherPerson?.email;

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages/send`,
        {
          conversation_id: selectedConversation.id,
          job_id: selectedConversation.job_id,
          sender_id: userId,
          sender_name: user.full_name || user.email,
          receiver_id: otherUserId,
          receiver_name: otherPerson?.name || otherPerson?.email,
          text: messageInput
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setCurrentMessages(prev => [...prev, response.data]);
      setConversations(prevConvs =>
        prevConvs.map(conv =>
          conv.id === selectedConversation.id
            ? {
                ...conv,
                last_message: messageInput,
                last_message_time: new Date().toISOString()
              }
            : conv
        )
      );

      setMessageInput('');

      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const filteredConversations = conversations.filter(conv => {
    // First apply search filter
    const matchesSearch =
      (conv.other_person?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (conv.job_title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (conv.job_id || '').toLowerCase().includes(searchQuery.toLowerCase());

    // Then apply unread filter if enabled
    if (filterUnread) {
      return matchesSearch && (conv.unread_count > 0);
    }

    return matchesSearch;
  });

  // Calculate total unread count for display
  const totalUnreadCount = conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentMessages]);

  // iOS keyboard: keep input visible by resizing container to visual viewport
  const chatContainerRef = useRef(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const onResize = () => {
      const container = chatContainerRef.current;
      if (!container) return;
      // Calculate available height accounting for keyboard
      const top = container.getBoundingClientRect().top + vv.offsetTop;
      const newHeight = vv.height - top;
      container.style.height = `${newHeight}px`;
      // Scroll messages to bottom when keyboard opens
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    };

    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onResize);
    };
  }, []);

  // Poll for new messages when a conversation is selected
  useEffect(() => {
    if (!selectedConversation) return;

    const pollMessages = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/messages/conversation/${selectedConversation.id}?user_email=${user.email || 'test@shootzu.com'}`
        );
        const newMessages = response.data.messages || [];

        // Only update if messages changed (compare length and last message id to deduplicate)
        const lastServerId = newMessages[newMessages.length - 1]?.id || newMessages[newMessages.length - 1]?._id;
        const lastLocalId = currentMessages[currentMessages.length - 1]?.id || currentMessages[currentMessages.length - 1]?._id;
        if (newMessages.length !== currentMessages.length || lastServerId !== lastLocalId) {
          setCurrentMessages(newMessages);
          // Also update the conversation list's unread count
          setConversations(prev => prev.map(conv =>
            conv.id === selectedConversation.id ? { ...conv, unread_count: 0 } : conv
          ));
        }
      } catch (error) {
        console.log('Message poll failed:', error);
      }
    };

    // Poll every 3 seconds for new messages
    const interval = setInterval(pollMessages, 3000);

    return () => clearInterval(interval);
  }, [selectedConversation?.id, currentMessages.length, user.email]);

  // Poll for new conversations (and unread counts) every 10 seconds
  useEffect(() => {
    const pollConversations = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/messages/conversations?user_email=${user.email || 'test@shootzu.com'}&limit=20&page=1`
        );

        const newConversations = response.data.conversations || [];

        // Merge new conversations with existing ones, preserving selection
        setConversations(prev => {
          // Create a map of existing conversations
          const existingMap = new Map(prev.map(c => [c.id, c]));

          // Update with new data
          newConversations.forEach(newConv => {
            existingMap.set(newConv.id, newConv);
          });

          // Convert back to array and sort with the SAME ordering as the initial load
          return sortConversations(Array.from(existingMap.values()));
        });
      } catch (error) {
        console.log('Conversation poll failed:', error);
      }
    };

    // Only poll if we have a user
    if (!user.email) return;

    const interval = setInterval(pollConversations, 10000);

    return () => clearInterval(interval);
  }, [user.email]);

  // Build the job-banner meta line: date · location · total (matches prototype jobMeta)
  const buildJobMeta = (conv) => {
    const bits = [];
    if (conv.job_date) bits.push(formatDate(conv.job_date));
    if (conv.job_location) bits.push(getCityState(conv.job_location));
    if (conv.job_total) bits.push(money(conv.job_total));
    return bits.join('  ·  ');
  };

  // Avatar tile: photo when available, gradient + initial fallback
  const Avatar = ({ person, size }) => {
    const [errored, setErrored] = useState(false);
    const px = `${size}px`;
    const radius = 999; // Command Deck — circular avatars
    const showImg = person?.profile_photo_url && !errored;
    return (
      <div
        className="fj-name"
        style={{
          position: 'relative',
          width: px,
          height: px,
          borderRadius: radius,
          background: avatarGradient(person?.name),
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: size >= 44 ? 16 : 15,
          overflow: 'hidden',
          flex: '0 0 auto'
        }}
      >
        {showImg ? (
          <img
            src={person.profile_photo_url}
            alt={person?.name || ''}
            onError={() => setErrored(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          initial(person?.name)
        )}
      </div>
    );
  };

  const activeJobMeta = selectedConversation ? buildJobMeta(selectedConversation) : '';

  return (
    <div className="min-h-screen" style={{ background: '#0A0E1A' }}>
      <Sidebar onLogout={onLogout} />
      <Header />

      <div className="cd-root fj-root sidebar-content pt-16 lg:pt-20">
        {/* Animated bloom blobs */}
        <div className="cd-bloom-wrap">
          <div className="cd-bloom cd-bloom-1" />
          <div className="cd-bloom cd-bloom-2" />
        </div>

        <div className="px-4 sm:px-6 lg:px-8 py-4 lg:py-6" style={{ position: 'relative', zIndex: 1 }}>
          <div className="max-w-7xl mx-auto">
            {/* Title strip */}
            <div className="flex items-center gap-3" style={{ marginBottom: 14 }}>
              <span className="cd-display" style={{ fontWeight: 800, fontSize: 22, color: '#fff', letterSpacing: '-.5px' }}>
                Messages
              </span>
              {totalUnreadCount > 0 && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontWeight: 700,
                    fontSize: 11,
                    color: '#C98AD4',
                    background: 'rgba(120,40,124,.25)',
                    padding: '4px 10px',
                    borderRadius: 999
                  }}
                >
                  {totalUnreadCount} unread
                </span>
              )}
            </div>

            <div
              ref={chatContainerRef}
              className="overflow-hidden flex"
              style={{
                height: 'calc(100dvh - 178px)',
                minHeight: '500px',
                background: 'rgba(255,255,255,.04)',
                border: '1px solid rgba(255,255,255,.08)',
                borderRadius: 16,
                boxShadow: '0 30px 70px -40px rgba(0,0,0,.7)'
              }}
            >

              {/* ================= LEFT — CONVERSATION LIST ================= */}
              <div
                className={`${showConversationList ? 'flex' : 'hidden'} md:flex flex-col w-full md:w-[340px] md:flex-shrink-0`}
                style={{ borderRight: '1px solid rgba(255,255,255,.07)', minHeight: 0 }}
              >
                {/* Header: search + filter row */}
                <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
                  <div
                    className="flex items-center gap-2.5"
                    style={{
                      background: 'rgba(255,255,255,.04)',
                      border: '1px solid rgba(255,255,255,.12)',
                      borderRadius: 11,
                      padding: '9px 12px'
                    }}
                  >
                    <Search className="w-4 h-4 flex-shrink-0" style={{ color: 'rgba(255,255,255,.4)' }} />
                    <input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search name, job or ID"
                      className="w-full bg-transparent outline-none cd-input-plain"
                      style={{ fontSize: 13, color: '#fff' }}
                    />
                  </div>

                  {/* Filter row: All · Unread · Archived */}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => {
                        setFilterUnread(false);
                        setFilterArchived(false);
                        loadConversations(1, false, false);
                      }}
                      className="cd-msg-pill"
                      style={{
                        flex: 1,
                        justifyContent: 'center',
                        borderRadius: 999,
                        padding: '7px',
                        background: (!filterUnread && !filterArchived) ? '#28C0D0' : 'transparent',
                        border: (!filterUnread && !filterArchived) ? 'none' : '1px solid rgba(255,255,255,.12)',
                        color: (!filterUnread && !filterArchived) ? '#0C1120' : 'rgba(255,255,255,.7)',
                        fontWeight: 700,
                        fontSize: 12,
                        cursor: 'pointer'
                      }}
                    >
                      All
                    </button>

                    <button
                      onClick={() => {
                        const newValue = !filterUnread;
                        setFilterUnread(newValue);
                        setFilterArchived(false);
                        loadConversations(1, newValue, false);
                      }}
                      className="cd-msg-pill flex items-center gap-1.5"
                      style={{
                        flex: 1,
                        justifyContent: 'center',
                        borderRadius: 999,
                        padding: '7px',
                        background: filterUnread ? '#28C0D0' : 'transparent',
                        border: filterUnread ? 'none' : '1px solid rgba(255,255,255,.12)',
                        color: filterUnread ? '#0C1120' : 'rgba(255,255,255,.7)',
                        fontWeight: 600,
                        fontSize: 12,
                        cursor: 'pointer'
                      }}
                    >
                      {filterUnread ? <MailOpen className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />}
                      <span>Unread</span>
                      {totalUnreadCount > 0 && (
                        <span
                          className="fj-name"
                          style={{
                            minWidth: 16,
                            height: 16,
                            padding: '0 4px',
                            borderRadius: 999,
                            color: filterUnread ? '#0C1120' : '#C98AD4',
                            fontSize: 10,
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {totalUnreadCount}
                        </span>
                      )}
                    </button>

                    <button
                      onClick={() => {
                        const newValue = !filterArchived;
                        setFilterArchived(newValue);
                        setFilterUnread(false);
                        loadConversations(1, false, newValue);
                      }}
                      title="Archived"
                      className="cd-msg-pill flex items-center"
                      style={{
                        flex: '0 0 auto',
                        justifyContent: 'center',
                        borderRadius: 999,
                        padding: '7px 11px',
                        background: filterArchived ? '#28C0D0' : 'transparent',
                        border: filterArchived ? 'none' : '1px solid rgba(255,255,255,.12)',
                        color: filterArchived ? '#0C1120' : 'rgba(255,255,255,.7)',
                        cursor: 'pointer'
                      }}
                    >
                      <Archive className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* List */}
                <div className="fjscroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 8 }}>
                  {loading ? (
                    <div className="text-center" style={{ padding: '40px 20px', color: 'rgba(255,255,255,.5)', fontSize: 13 }}>
                      Loading…
                    </div>
                  ) : filteredConversations.length === 0 ? (
                    <div className="flex flex-col items-center text-center" style={{ padding: '50px 20px', color: 'rgba(255,255,255,.5)', fontSize: 13.5 }}>
                      <MessageSquare className="w-8 h-8 mb-3" style={{ color: 'rgba(255,255,255,.3)' }} />
                      {filterArchived
                        ? 'No archived conversations.'
                        : filterUnread
                          ? 'No unread conversations.'
                          : 'No conversations here.'}
                    </div>
                  ) : (
                    <>
                      {filteredConversations.map((conv) => {
                        const isActive = selectedConversation?.id === conv.id;
                        const isUnread = conv.unread_count > 0;
                        const isArchivedRow = filterArchived || conv.is_archived;
                        return (
                          <div
                            key={conv.id}
                            onClick={() => handleSelectConversation(conv)}
                            className={`group cd-conv-row${isActive ? ' cd-conv-active' : ''}`}
                            style={{
                              position: 'relative',
                              display: 'flex',
                              gap: 11,
                              padding: '11px 12px',
                              borderRadius: 13,
                              cursor: 'pointer',
                              marginBottom: 3,
                              background: isActive ? 'rgba(40,192,208,.1)' : 'transparent',
                              border: isActive ? '1px solid rgba(40,192,208,.28)' : '1px solid transparent',
                              transition: 'background .14s'
                            }}
                          >
                            <Avatar person={conv.other_person} size={44} />

                            <div style={{ flex: 1, minWidth: 0 }}>
                              {/* Top line: name + time */}
                              <div className="flex items-center gap-2">
                                <span
                                  className="fj-name"
                                  style={{
                                    fontWeight: 700,
                                    fontSize: 13.5,
                                    color: '#fff',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1
                                  }}
                                >
                                  {conv.other_person?.name || 'Unknown'}
                                </span>
                                <span style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', flex: '0 0 auto' }}>
                                  {conv.display_time || formatMessageTime(conv.last_message_time || conv.last_message_at)}
                                </span>
                              </div>

                              {/* Job line: Briefcase + job_title (aqua) */}
                              {conv.job_title && (
                                <div
                                  className="flex items-center gap-1.5"
                                  style={{
                                    fontSize: 11.5,
                                    color: '#28C0D0',
                                    fontWeight: 600,
                                    margin: '1px 0 3px',
                                    overflow: 'hidden'
                                  }}
                                >
                                  <Briefcase className="w-3 h-3 flex-shrink-0" />
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {conv.job_title}
                                  </span>
                                </div>
                              )}

                              {/* Bottom line: preview + unread badge / archive affordance */}
                              <div className="flex items-center gap-2">
                                <span
                                  style={{
                                    flex: 1,
                                    fontSize: 12.5,
                                    color: isUnread ? '#fff' : 'rgba(255,255,255,.55)',
                                    fontWeight: isUnread ? 600 : 400,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {conv.last_message || 'No messages yet'}
                                </span>

                                {/* Hover archive / restore affordance */}
                                {isArchivedRow ? (
                                  <button
                                    onClick={(e) => handleRestoreConversation(e, conv.id)}
                                    title="Restore conversation"
                                    className="cd-iconbtn opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 flex items-center justify-center"
                                    style={{ width: 26, height: 26, borderRadius: 8, background: 'rgba(12,17,32,.6)', border: '1px solid rgba(255,255,255,.16)', color: 'rgba(255,255,255,.7)' }}
                                  >
                                    <ArchiveRestore className="w-3.5 h-3.5" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => handleArchiveConversation(e, conv.id)}
                                    title="Archive conversation"
                                    className="cd-iconbtn opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 flex items-center justify-center"
                                    style={{ width: 26, height: 26, borderRadius: 8, background: 'rgba(12,17,32,.6)', border: '1px solid rgba(255,255,255,.16)', color: 'rgba(255,255,255,.7)' }}
                                  >
                                    <Archive className="w-3.5 h-3.5" />
                                  </button>
                                )}

                                {isUnread && (
                                  <span
                                    className="fj-name"
                                    style={{
                                      flex: '0 0 auto',
                                      minWidth: 18,
                                      height: 18,
                                      padding: '0 5px',
                                      borderRadius: 999,
                                      background: '#78287C',
                                      color: '#fff',
                                      fontSize: 10.5,
                                      fontWeight: 700,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    {conv.unread_count}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {hasMoreConversations && (
                        <div className="flex justify-center" style={{ padding: '12px 0' }}>
                          <button
                            onClick={loadMoreConversations}
                            className="cd-btn"
                            style={{ padding: '9px 16px', fontSize: 12.5 }}
                          >
                            Load more
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* ================= RIGHT — CHAT THREAD ================= */}
              <div
                className={`${showConversationList ? 'hidden' : 'flex'} md:flex flex-1 flex-col`}
                style={{ minWidth: 0, minHeight: 0, background: 'transparent' }}
              >
                {selectedConversation ? (
                  <>
                    {/* Thread header */}
                    <div
                      className="flex items-center gap-3 flex-shrink-0"
                      style={{
                        padding: '14px 20px',
                        background: 'transparent',
                        borderBottom: '1px solid rgba(255,255,255,.07)'
                      }}
                    >
                      <button
                        onClick={handleBackToList}
                        className="md:hidden flex items-center justify-center flex-shrink-0"
                        style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.12)', color: '#fff' }}
                      >
                        <ArrowLeft className="w-[17px] h-[17px]" />
                      </button>

                      <Avatar person={selectedConversation.other_person} size={44} />

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="fj-name" style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>
                          {selectedConversation.other_person?.name || 'Unknown'}
                        </div>
                        {selectedConversation.job_title && (
                          <div
                            style={{
                              fontSize: 12,
                              color: 'rgba(255,255,255,.5)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {selectedConversation.job_title}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Job banner — hidden for admin conversations (no job_id) */}
                    {selectedConversation.job_id && selectedConversation.job_title && (
                      <div
                        className="flex items-center gap-3 flex-shrink-0"
                        style={{
                          margin: '14px 20px 0',
                          padding: '14px 18px',
                          borderRadius: 14,
                          background: 'rgba(40,192,208,.08)',
                          border: '1px solid rgba(40,192,208,.22)'
                        }}
                      >
                        <div
                          className="flex items-center justify-center flex-shrink-0"
                          style={{ width: 40, height: 40, borderRadius: 11, background: 'rgba(40,192,208,.16)', color: '#28C0D0' }}
                        >
                          <Briefcase className="w-5 h-5" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 14,
                              color: '#fff',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {selectedConversation.job_title}
                          </div>
                          {activeJobMeta && (
                            <div
                              className="flex items-center gap-3"
                              style={{
                                fontSize: 12,
                                color: 'rgba(255,255,255,.55)',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {selectedConversation.job_date && (
                                <span className="flex items-center gap-1 flex-shrink-0">
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(selectedConversation.job_date)}
                                </span>
                              )}
                              {selectedConversation.job_location && (
                                <span className="flex items-center gap-1 flex-shrink-0" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  <MapPin className="w-3 h-3" />
                                  {getCityState(selectedConversation.job_location)}
                                </span>
                              )}
                              {selectedConversation.job_total && (
                                <span className="flex-shrink-0" style={{ fontWeight: 600, color: '#67C6B4' }}>
                                  {money(selectedConversation.job_total)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={handleViewJobDetails}
                          className="cd-iconbtn flex-shrink-0"
                          style={{
                            fontWeight: 600,
                            fontSize: 12.5,
                            color: '#fff',
                            background: 'rgba(255,255,255,.06)',
                            border: '1px solid rgba(255,255,255,.16)',
                            borderRadius: 10,
                            padding: '9px 16px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          View details
                        </button>
                      </div>
                    )}

                    {/* Messages */}
                    <div
                      className="fjscroll"
                      style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 4 }}
                    >
                      {currentMessages.map((message, index) => {
                        const user_number = user.user_number ? String(user.user_number) : null;
                        const isCurrentUser = message.sender_id === user.id ||
                                             message.sender_id === user.email ||
                                             message.sender_id === user_number;

                        const prev = currentMessages[index - 1];
                        // Date separator when display_date changes
                        const showDateSeparator = index === 0 || (
                          message.display_date &&
                          prev?.display_date !== message.display_date
                        );
                        // Show sender name above theirs only, first of a run
                        const showName = !isCurrentUser && (
                          !prev ||
                          prev.sender_id !== message.sender_id ||
                          prev.display_date !== message.display_date
                        );

                        return (
                          <div key={message.id}>
                            {showDateSeparator && message.display_date && (
                              <div className="flex justify-center" style={{ margin: '10px 0' }}>
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: 'rgba(255,255,255,.4)',
                                    background: 'rgba(255,255,255,.06)',
                                    padding: '4px 12px',
                                    borderRadius: 999
                                  }}
                                >
                                  {message.display_date}
                                </span>
                              </div>
                            )}

                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
                                marginBottom: 9
                              }}
                            >
                              {showName && message.sender_name && (
                                <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,.5)', margin: '0 0 3px 4px' }}>
                                  {message.sender_name}
                                </span>
                              )}
                              <div
                                style={{
                                  maxWidth: '74%',
                                  padding: '11px 14px',
                                  borderRadius: isCurrentUser ? '16px 16px 5px 16px' : '16px 16px 16px 5px',
                                  background: isCurrentUser ? '#ECEEF2' : 'rgba(255,255,255,.06)',
                                  border: isCurrentUser ? 'none' : '1px solid rgba(255,255,255,.08)',
                                  color: isCurrentUser ? '#0C1120' : '#fff',
                                  fontWeight: isCurrentUser ? 500 : 400,
                                  fontSize: 13.5,
                                  lineHeight: 1.5,
                                  textAlign: 'left',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  boxShadow: isCurrentUser
                                    ? '0 8px 18px -14px rgba(0,0,0,.6)'
                                    : 'none'
                                }}
                              >
                                {linkifyText(message.text)}
                              </div>
                              <div className="flex items-center gap-1.5" style={{ margin: '4px 4px 0' }}>
                                <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,.4)' }}>
                                  {message.display_time || formatMessageTime(message.created_at, message.display_time, message.display_date)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Composer */}
                    <div
                      className="flex-shrink-0"
                      style={{ padding: '14px 20px 18px', borderTop: '1px solid rgba(255,255,255,.07)' }}
                    >
                      <div
                        className="flex items-end gap-2.5"
                        style={{
                          background: 'rgba(255,255,255,.05)',
                          border: '1px solid rgba(255,255,255,.12)',
                          borderRadius: 14,
                          padding: '8px 8px 8px 14px'
                        }}
                      >
                        <AutoGrowTextarea
                          value={messageInput}
                          onChange={(e) => setMessageInput(e.target.value)}
                          onSubmit={handleSendMessage}
                          maxRows={5}
                          placeholder="Write a message…"
                          className="flex-1 cd-input-plain"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            padding: '8px 0',
                            fontSize: 13.5,
                            color: '#fff',
                            maxHeight: 120,
                            lineHeight: 1.5
                          }}
                        />
                        <button
                          onClick={handleSendMessage}
                          disabled={!messageInput.trim() || sendingMessage}
                          className="cd-send flex items-center justify-center flex-shrink-0"
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 11,
                            border: 'none',
                            cursor: !messageInput.trim() || sendingMessage ? 'not-allowed' : 'pointer',
                            background: '#28C0D0',
                            color: '#0C1120',
                            opacity: !messageInput.trim() || sendingMessage ? 0.5 : 1,
                            transition: 'transform .14s'
                          }}
                        >
                          <Send className="w-[19px] h-[19px]" />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div
                    className="flex-1 flex flex-col items-center justify-center text-center"
                    style={{ gap: 12, color: 'rgba(255,255,255,.5)', padding: 30 }}
                  >
                    <div
                      className="flex items-center justify-center"
                      style={{ width: 64, height: 64, borderRadius: 20, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.08)', color: 'rgba(255,255,255,.5)' }}
                    >
                      <MessageSquare className="w-7 h-7" />
                    </div>
                    <div className="cd-display" style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>
                      Select a conversation
                    </div>
                    <div style={{ fontSize: 13, maxWidth: '30ch' }}>
                      Choose a chat on the left to see the full thread and reply.
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Job Details Modal - Reusable Component */}
      {fullJobData && (
        <JobDetailsModal
          job={fullJobData}
          isOpen={showJobModal}
          onClose={() => {
            setShowJobModal(false);
            setFullJobData(null);
          }}
          isDark={isDark}
          showApplyButton={false}
          hideHiredCrew={true}
          messagesContext={true}
          isJobOwner={fullJobData?.owner?.email === user.email || fullJobData?.owner_email === user.email}
          wasHired={fullJobData?.hiredCrew?.email === user.email ? true : (fullJobData?.status === 'HIRED' || fullJobData?.status === 'PAID_COMPLETED' || fullJobData?.status === 'hired' || fullJobData?.status === 'completed') && fullJobData?.hiredCrew && fullJobData?.hiredCrew?.email !== user.email ? false : null}
        />
      )}
    </div>
  );
};

export default MessagesRedesigned;
